/**
 * Created by katarinababic on 22.11.24.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { BreathingEntry } from './BreathingEntry';
import { BreathingTechniqueStep } from './BreathingTechniqueStep';
import { translate } from '../../Store/System/LocalizationStore';
import { ActivityLog } from '../Activity/ActivityLog';

export type MusicFile = { key: string; title: string; source: () => ReturnType<typeof require> };

export const TimeoutSoundSelection: MusicFile[] = [
  {
    title: String(translate('explore.timeoutTab.noSound')),
    key: 'no-sound',
    source: () => undefined,
  },
  {
    title: 'Distant Thunder and Water Dripping from Forest Canopy',
    key: 'Distant-Thunder-and-Water-Dripping-from-Forest-Canopy.mp3',
    source: () => require('../../Assets/Audio/Breathing/Distant_Thunder_and_Water_Dripping_from_Forest_Canopy.mp3'),
  },
  {
    title: 'Exotic Swamp Birds',
    key: 'Exotic-Swamp-Birds.mp3',
    source: () => require('../../Assets/Audio/Breathing/Exotic_Swamp_Birds.mp3'),
  },
  {
    title: 'Ocean Waves',
    key: 'Ocean-Waves_1.mp3',
    source: () => require('../../Assets/Audio/Breathing/Ocean_Waves.mp3'),
  },
  {
    title: 'Light Rain Falling on Forest Floor',
    key: 'Light-Rain-Falling-on-Forest-Floor_1.mp3',
    source: () => require('../../Assets/Audio/Breathing/Light_Rain_Falling_on_Forest_Floor.mp3'),
  },
  {
    title: 'Morning Bird Songs',
    key: 'Morning Bird Songs.mp3',
    source: () => require('../../Assets/Audio/Breathing/Morning_Bird_Songs.mp3'),
  },
  {
    title: 'Mountain Atmosphere',
    key: 'Mountain-Atmosphere_1.mp3',
    source: () => require('../../Assets/Audio/Breathing/Mountain_Atmosphere.mp3'),
  },
];

export type BreathingSession = {
  entry: BreathingEntry;
  singleCycleDuration: number;
  duration: number;
  startDateTime: Date | undefined;
  endDateTime: Date | undefined;
  currentCommand: BreathingTechniqueStep | undefined;
  selectedSound: MusicFile | undefined;
  readonly totalCycles: number;
  readonly totalDuration: number;
  readonly percentageCompleted: number;
  readonly totalDeepBreaths: number;
  readonly notStarted: boolean;
  readonly started: boolean;
  readonly active: boolean;
  readonly completed: boolean;
  setDuration(value: number): void;
  startSession(): Date;
  endSession(): Date | undefined;
  setSelectedSound(musicFile?: MusicFile): void;
  createActivityLog(): ActivityLog | undefined;
};

export function createBreathingSession(entry: BreathingEntry): BreathingSession {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  const singleCycleDuration = entry.technique.reduce((total, t) => total + t.duration, 0);

  const session: BreathingSession = makeAutoObservable({
    entry,
    singleCycleDuration,
    duration: Math.floor(60 / singleCycleDuration) * singleCycleDuration,
    startDateTime: undefined as Date | undefined,
    endDateTime: undefined as Date | undefined,
    currentCommand: undefined as BreathingTechniqueStep | undefined,
    selectedSound:
      TimeoutSoundSelection.find((m) => entry.backgroundVideos.some((v) => v.name === m.key)) ??
      TimeoutSoundSelection.find((m) => m.key === 'Distant-Thunder-and-Water-Dripping-from-Forest-Canopy.mp3'),

    get totalCycles(): number {
      return Math.floor(this.duration / this.singleCycleDuration);
    },

    get totalDuration(): number {
      return (this.endDateTime ?? new Date()).valueOf() - (this.startDateTime ?? new Date()).valueOf();
    },

    get percentageCompleted(): number {
      return Math.min(((this.totalDuration / 1000) * 100) / this.duration, 100);
    },

    get totalDeepBreaths(): number {
      return Math.max(Math.floor(Math.floor(this.totalDuration / 1000) / this.singleCycleDuration), 1);
    },

    get notStarted(): boolean {
      return !this.startDateTime && !this.endDateTime;
    },

    get started(): boolean {
      return !!this.startDateTime;
    },

    get active(): boolean {
      return !!this.startDateTime && !this.endDateTime;
    },

    get completed(): boolean {
      return !!this.startDateTime && !!this.endDateTime;
    },

    setDuration(value: number) {
      this.duration = Math.floor(value / this.singleCycleDuration) * this.singleCycleDuration;
    },

    startSession(): Date {
      if (this.completed) {
        runInAction(() => {
          this.startDateTime = undefined;
          this.endDateTime = undefined;
          this.currentCommand = undefined;
          timeout && clearTimeout(timeout);
        });
      }
      if (!this.startDateTime) {
        this.startDateTime = new Date();
        scheduleCommands();
      }
      return this.startDateTime;
    },

    endSession(): Date | undefined {
      timeout && clearTimeout(timeout);
      if (this.startDateTime && !this.endDateTime) {
        this.endDateTime = new Date();
      }
      return this.endDateTime;
    },

    setSelectedSound(musicFile?: MusicFile) {
      this.selectedSound = musicFile;
    },

    createActivityLog(): ActivityLog | undefined {
      if (this.startDateTime && this.endDateTime) {
        return this.entry.createActivityLog(this.startDateTime, this.endDateTime, {
          PERCENTAGE_COMPLETED: this.percentageCompleted,
          TOTAL_BREATHS: this.totalDeepBreaths,
        });
      }
    },
  });

  function getMultipliedCommands(): BreathingTechniqueStep[] {
    return [...Array(session.totalCycles)].reduce(
      (prev) => [...prev, ...session.entry.technique],
      [] as BreathingTechniqueStep[],
    );
  }

  function scheduleCommands() {
    const multipliedCommands = getMultipliedCommands();
    scheduleCommand(multipliedCommands, 0);
  }

  function scheduleCommand(commands: BreathingTechniqueStep[], index: number) {
    const command = commands[index];
    if (command) {
      const nextIndex = index + 1;
      const nextCommand = commands[index + 1];
      runInAction(() => (session.currentCommand = command));
      timeout && clearTimeout(timeout);
      timeout = setTimeout(() => {
        if (nextCommand) {
          scheduleCommand(commands, nextIndex);
        } else {
          session.endSession();
        }
      }, command.duration * 1000);
    }
  }

  return session;
}
